import {
   PractitionerMedicalServiceParameters,
   PractitionerMedicalServiceUpdateCommand,
   PractitionerSettlementParameters,
} from '@dreryk/egabinet-private-medical-service-frontend-api';
import { Money } from '@dreryk/money-common-api';

import { useCqrsCommand } from '@dreryk/command-query-react-common';
import { dispatchSnackbarPositiveEvent } from '@dreryk/react-components';
import { Choice } from '@dreryk/watermelon';

import { practitionerMedicalServiceTranslationKeys } from '../../../../translations';
import { createMedicalServiceSettlementTypeVisitor } from '../../../shared/settlement/settlement.utils';

import { PractitionerMedicalServiceUpdateFormBean } from './PractitionerMedicalServiceUpdateForm';

type PractitionerMedicalServiceUpdateActionRenderProps = {
   updatePractitionerMedicalService: (formData: PractitionerMedicalServiceUpdateFormBean) => Promise<void>;
};

type PractitionerMedicalServiceUpdateActionProps = {
   children: (renderProps: PractitionerMedicalServiceUpdateActionRenderProps) => JSX.Element;
   onSuccess: VoidFunction;
};

export const PractitionerMedicalServiceUpdateAction = (
   props: PractitionerMedicalServiceUpdateActionProps
): JSX.Element => {
   const { executeAsync } = useCqrsCommand<void, PractitionerMedicalServiceUpdateFormBean>(
      (parameters) => {
         const command = new PractitionerMedicalServiceUpdateCommand();
         command.medicalServiceId = parameters.id;

         [command.parameters] = parameters.practitioners!.map(
            (practitioner): PractitionerMedicalServiceParameters => {
               const practitionerSettlementTypeVisitor = createMedicalServiceSettlementTypeVisitor(
                  parameters.settlementType
               );

               if (parameters.usingDefaultConfiguration) {
                  return {
                     practitionerId: practitioner.id,
                     usingDefaultConfiguration: true,
                  };
               }

               return {
                  practitionerId: practitioner.id,
                  usingDefaultConfiguration: false,
                  individualPrice: Choice.of(Money.of(parameters.defaultPrice!)),
                  settlementParameters: Choice.of(
                     practitionerSettlementTypeVisitor<PractitionerSettlementParameters>({
                        visitNone: () => ({
                           settlementType: 'NONE',
                        }),
                        visitFixed: () => ({
                           settlementType: 'FIXED',
                           fixedValue: Choice.of(Money.of(parameters.fixedValue!)),
                        }),
                        visitProportional: () => ({
                           settlementType: 'PROPORTIONAL',
                           proportionalValue: Choice.of(parameters.proportionalValue!),
                        }),
                     })
                  ),
               };
            }
         );

         return command;
      },
      {
         onSuccess: () => {
            props.onSuccess();
            dispatchSnackbarPositiveEvent(practitionerMedicalServiceTranslationKeys.update.confirmationText);
         },
      }
   );

   return props.children({
      updatePractitionerMedicalService: executeAsync,
   });
};
